/** @jsx jsx */
import React from 'react'
import { jsx } from 'theme-ui'
import { PaletteProvider } from '../../../utils/palette'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'

import { leftPad } from '../../../utils/string'

import { EVEntry } from '.'
import ModMarker, { displayModMarker } from './module-marker'
import { ConditionalWrapper } from '../../../utils/conditional-wrapper'

import { unnest, sequentialGroupBy } from '../../../utils/arr'
import { contentTypeId } from '../../../utils/contentful'

import SmartContainer from './smart-container2'
import ConditionalHydrate from '../conditional-hydrate'
import { useDoHydrateComponent, usePalette, useOptions, useIncludes, getEntity } from '../selectors'

const ReactElementOrModule = ({ item, index, moduleIndexPrefix = '', childOfPage }) => (
  <Choose>
    <When condition={React.isValidElement(item)}>
      <item.type {...item.props} />
    </When>
    <Otherwise>
      <EVEntry
        {...item}
        moduleIndex={`${moduleIndexPrefix}-${leftPad(index)}`}
        track={childOfPage}
      />
    </Otherwise>
  </Choose>
)

const PaletteWrapper = ({ palette, children }) => (
  <PaletteProvider value={usePalette(palette)}>{children}</PaletteProvider>
)

export const RichText = ({ content: document, style = {}, optionOverrides, ...entity }) => {
  const includes = useIncludes()
  const options = useOptions()

  const config = { optionOverrides }
  const optionsWithOverrides = options(config)

  const doHydrateComponent = useDoHydrateComponent(entity.sys?.id)
  const doHydrate = entity.forceHydrate || !entity.sys || doHydrateComponent

  if (!document) return <></>
  // For storybook
  if (typeof document === 'string') return <div>{document}</div>

  const untrimmedContentArr = Array.isArray(document)
    ? document
    : Array.isArray(document.content)
    ? document.content
    : []

  const contentArr = untrimmedContentArr
    .reduce((acc, entry) => {
      const { nodeType, content = [] } = entry

      // When trimming rich content, we should only trim items that are
      // not assets or entries
      if (nodeType === 'embedded-entry-block' || nodeType === 'embedded-asset-block') {
        return acc.concat(entry)
      }

      const trimmedContent = content.filter(({ value }) => !/^$/.test(value))
      return trimmedContent.length ? acc.concat({ ...entry, content: trimmedContent }) : acc
    }, [])
    .map((mod, i) => ({ ...mod, index: i }))

  const moduleListLength = contentArr.length

  const paletteGroups = sequentialGroupBy(
    contentArr,
    (mod) => contentTypeId(getEntity(mod.data.target, includes)) === 'palette',
    (mod) => mod.data.target,
  )

  const groupedBlocks = paletteGroups.map(([palette, content]) => [
    palette,
    unnest(content, ({ nodeType }) => nodeType === 'embedded-entry-block'),
  ])

  return (
    <ConditionalWrapper condition={!doHydrate} wrapper={ConditionalHydrate}>
      {displayModMarker() && <ModMarker entity={entity} />}
      {groupedBlocks.map(([palette, groups], i) => {
        return groups.map((group, j) => {
          const content = documentToReactComponents(
            { content: [].concat(group) },
            optionsWithOverrides,
          )
          const isEmbeddedEntry = group.nodeType === 'embedded-entry-block'
          const first = i === 0 && j === 0

          const key = `rich-pallete_${leftPad(i)}-group_${leftPad(j)}`

          if (isEmbeddedEntry) {
            return (
              <ConditionalWrapper
                key={key}
                condition={!!palette}
                wrapper={PaletteWrapper}
                palette={palette}
              >
                <EVEntry
                  {...content[0]}
                  moduleIndex={key}
                  track={false}
                  removeTopSpacing={first}
                  removeBottomSpacing={group.index + 1 === moduleListLength}
                />
              </ConditionalWrapper>
            )
          }

          return (
            <ConditionalWrapper
              key={key}
              condition={!!palette}
              wrapper={PaletteWrapper}
              palette={palette}
            >
              {displayModMarker() && <ModMarker entity={entity} />}
              <SmartContainer styling={entity.styling} textContent={true} sx={{ ...style }}>
                <For each='item' index='index' of={content}>
                  <ReactElementOrModule
                    index={index}
                    key={index}
                    item={item}
                    moduleIndexPrefix='key'
                    childOfPage={false}
                  />
                </For>
              </SmartContainer>
            </ConditionalWrapper>
          )
        })
      })}
    </ConditionalWrapper>
  )
}

export const RichPage = (props) => RichText({ ...props, childOfPage: true })
